import React from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../../context/ThemeContext';

const ContactSection: React.FC = () => {
  const { themeConfig } = useTheme();

  const inputClass =
    'w-full rounded-lg border border-canvas-border bg-canvas-deep px-3.5 py-2.5 text-ink-main placeholder:text-ink-muted focus:border-neon-cyan/60 focus:outline-none focus:ring-2 focus:ring-neon-cyan/30 transition';

  return (
    <section id="contact" className={themeConfig.layout?.sectionStyle}>
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-80px' }}
        transition={{ duration: 0.6 }}
        className="mb-12 max-w-2xl"
      >
        <div className="mb-3 flex items-center gap-2">
          <span className="h-px w-6 bg-neon-cyan/60" />
          <span className="font-mono text-xs uppercase tracking-[0.25em] text-neon-cyan">
            {'// trigger node'}
          </span>
        </div>
        <h2 className="font-display text-3xl font-bold leading-tight text-ink-main sm:text-4xl">
          Let&apos;s wire up your <span className="gradient-text">automation</span>
        </h2>
        <p className="mt-3 text-lg text-ink-soft">
          Got a repetitive process, a CRM to tame, or a workflow in mind? Send
          the trigger — I&apos;ll handle the actions.
        </p>
      </motion.div>

      <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
        {/* Contact info node */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="n8n-node p-6"
        >
          <span className="node-port in" />
          <h3 className="mb-6 font-display text-xl font-semibold text-ink-main">
            Contact info
          </h3>

          <div className="space-y-5">
            <ContactRow
              label="Email"
              value="sharmachaitanya945@gmail.com"
              href="mailto:sharmachaitanya945@gmail.com"
              icon={
                <path
                  d="M3 6.5h18v11H3v-11zm0 0l9 6 9-6"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  fill="none"
                  strokeLinejoin="round"
                />
              }
            />
            <ContactRow
              label="Location"
              value="New Delhi, India · Remote worldwide"
              icon={
                <>
                  <path d="M12 21s7-6 7-11a7 7 0 10-14 0c0 5 7 11 7 11z" stroke="currentColor" strokeWidth="1.6" fill="none" />
                  <circle cx="12" cy="10" r="2.4" stroke="currentColor" strokeWidth="1.6" fill="none" />
                </>
              }
            />
            <ContactRow
              label="Response time"
              value="Usually within a few hours"
              icon={
                <>
                  <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="1.6" fill="none" />
                  <path d="M12 8v4l3 2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                </>
              }
            />
          </div>

          <a
            href="https://calendly.com/sharmachaitanya945/meeting-with-me"
            target="_blank"
            rel="noopener noreferrer"
            className="neon-btn mt-8 w-full justify-center"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.8" />
              <path d="M3 9h18M8 3v4M16 3v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
            Book a free 30-min call
          </a>

          <div className="mt-8">
            <h4 className="mb-3 font-mono text-xs uppercase tracking-widest text-ink-muted">
              find me
            </h4>
            <div className="flex gap-3">
              {[
                { label: 'GitHub', href: 'https://github.com/sharmachaitanya945', d: 'M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z' },
                { label: 'LinkedIn', href: 'https://www.linkedin.com/in/sharmachaitanya945/', d: 'M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 11.001-4.124 2.062 2.062 0 01-.001 4.124zM7.119 20.452H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z' },
                { label: 'YouTube', href: 'https://www.youtube.com/@sharmachaitanya945', d: 'M23.498 6.186a2.993 2.993 0 00-2.107-2.117C19.24 3.5 12 3.5 12 3.5s-7.24 0-9.391.569A2.993 2.993 0 00.502 6.186C0 8.338 0 12 0 12s0 3.662.502 5.814a2.993 2.993 0 002.107 2.117C4.76 20.5 12 20.5 12 20.5s7.24 0 9.391-.569a2.993 2.993 0 002.107-2.117C24 15.662 24 12 24 12s0-3.662-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z' },
                { label: 'X', href: 'https://twitter.com/Chaitanyatweetx', d: 'M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84' },
              ].map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={s.label}
                  className="grid h-10 w-10 place-items-center rounded-lg border border-canvas-border bg-canvas-deep text-ink-soft transition-all hover:border-neon-cyan/60 hover:text-neon-cyan hover:shadow-neon-cyan"
                >
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d={s.d} />
                  </svg>
                </a>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Form node */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.15 }}
          viewport={{ once: true }}
          className="n8n-node p-6"
        >
          <span className="node-port out" />
          <h3 className="mb-6 font-display text-xl font-semibold text-ink-main">
            Send a message
          </h3>
          <form action="https://formsubmit.co/sharmachaitanya945@gmail.com" method="POST" className="space-y-5">
            <input type="hidden" name="_subject" value="New Portfolio Contact Form Submission!" />
            <input type="hidden" name="_captcha" value="false" />
            <input type="hidden" name="_next" value="https://sharmachaitanya945.com/thank-you.html" />
            <input
              type="hidden"
              name="_autoresponse"
              value="Thanks for reaching out! I'll get back to you as soon as possible."
            />

            <div>
              <label htmlFor="name" className="mb-1.5 block font-mono text-xs uppercase tracking-wide text-ink-soft">
                Name <span className="text-neon-magenta">*</span>
              </label>
              <input type="text" id="name" name="name" required className={inputClass} />
            </div>
            <div>
              <label htmlFor="email" className="mb-1.5 block font-mono text-xs uppercase tracking-wide text-ink-soft">
                Email <span className="text-neon-magenta">*</span>
              </label>
              <input type="email" id="email" name="email" required className={inputClass} />
            </div>
            <div>
              <label htmlFor="subject" className="mb-1.5 block font-mono text-xs uppercase tracking-wide text-ink-soft">
                Subject
              </label>
              <input type="text" id="subject" name="subject" className={inputClass} />
            </div>
            <div>
              <label htmlFor="message" className="mb-1.5 block font-mono text-xs uppercase tracking-wide text-ink-soft">
                Message <span className="text-neon-magenta">*</span>
              </label>
              <textarea id="message" name="message" rows={5} required className={inputClass}></textarea>
            </div>
            <button type="submit" className="neon-btn w-full justify-center">
              Run workflow →
            </button>
          </form>
        </motion.div>
      </div>
    </section>
  );
};

const ContactRow: React.FC<{
  label: string;
  value: string;
  href?: string;
  icon: React.ReactNode;
}> = ({ label, value, href, icon }) => (
  <div className="flex items-start gap-3">
    <span className="node-chip !h-10 !w-10 shrink-0 text-neon-cyan">
      <svg width="20" height="20" viewBox="0 0 24 24">{icon}</svg>
    </span>
    <div>
      <p className="font-mono text-[11px] uppercase tracking-wide text-ink-muted">{label}</p>
      {href ? (
        <a href={href} className="text-ink-main transition-colors hover:text-neon-cyan">
          {value}
        </a>
      ) : (
        <p className="text-ink-main">{value}</p>
      )}
    </div>
  </div>
);

export default ContactSection;
